import { buildPresetPackages, matchPresetFromPicks } from './resolve';

const norm = s =>
  String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

/** Build picks from the live form draft for one trade (API 9 + fixtures). */
export const picksFromDraft = (trade, draft) => {
  const items = [];
  const options = [];
  const scopeIds = [];
  const elementIds = [];
  const modifierAnswers = {};

  const captureMod = m => {
    const key = m.id || m.modifier_id;
    const ans = (key != null && draft?.modifiers?.[key]) || draft?.modifiers?.[m.id] || draft?.modifiers?.[m.modifier_id];
    if (!ans) return;
    const opts = ans.selected_options || [];
    const text = ans.text_value || '';
    if (!opts.length && !text) return;
    modifierAnswers[String(key)] = {
      selected_options: opts,
      text_value: text
    };
    if (m.id != null) modifierAnswers[String(m.id)] = modifierAnswers[String(key)];
    if (m.modifier_id != null) modifierAnswers[String(m.modifier_id)] = modifierAnswers[String(key)];
    opts.forEach(o => options.push(o));
    if (text) options.push(text);
  };

  (trade?.scopes || []).forEach(sc => {
    if (!draft?.scopes?.[sc.scope_id]) return;
    scopeIds.push(String(sc.scope_id));
    if (sc.scope_name) items.push(sc.scope_name);
    (sc.elements || []).forEach(el => {
      if (draft?.elements?.[el.id]) elementIds.push(String(el.id));
    });
    (sc.modifiers || []).forEach(captureMod);
  });
  (trade?.trade_modifiers || []).forEach(captureMod);

  return { items, options, scopeIds, elementIds, modifierAnswers };
};

const findAnswer = (answers, title) => {
  if (!answers || title == null) return undefined;
  if (Object.prototype.hasOwnProperty.call(answers, title)) return answers[title];
  const want = norm(title);
  const key = Object.keys(answers).find(k => norm(k) === want);
  return key != null ? answers[key] : undefined;
};

const toModifierValue = (modifier, answer) => {
  if (answer && typeof answer === 'object' && !Array.isArray(answer)) {
    return {
      selected_options: answer.selected_options || [],
      text_value: answer.text_value || ''
    };
  }
  const vals = [].concat(answer).map(String).filter(Boolean);
  if (modifier.modifier_type === 'multi_select') {
    const labels = (modifier.answer_options || modifier.options || []).map(o =>
      typeof o === 'string' ? o : o.label || o.value || ''
    );
    const selected = vals.filter(v => labels.some(l => norm(l) === norm(v)) || labels.length === 0);
    return { selected_options: selected.length ? selected : vals, text_value: '' };
  }
  if (modifier.modifier_type === 'subjective') {
    return { selected_options: [], text_value: vals[0] || '' };
  }
  return { selected_options: vals.slice(0, 1), text_value: '' };
};

const answerForModifier = (answersByModifierId, m) => {
  if (!answersByModifierId || !m) return undefined;
  const keys = [m.id, m.modifier_id].filter(v => v != null).map(String);
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(answersByModifierId, k)) return answersByModifierId[k];
  }
  return undefined;
};

const modifierWriteKey = m => m.id || m.modifier_id;

const elementDistinctiveOnOtherPreset = (cfg, preset, elId) =>
  (cfg?.presets || []).some(
    p =>
      String(p.id) !== String(preset.id) &&
      p.elementMap?.[String(elId)] === true &&
      preset.elementMap?.[String(elId)] !== true
  );

/**
 * Apply a preset in one action.
 * API 9: preset_scopes.scope_ids / elements / answers by modifier_id.
 * Fixtures: items by scope name + answers by modifier title.
 * Items no preset controls are left untouched.
 */
export const applyPresetToDraft = (draft, trade, preset, cfg) => {
  if (!draft || !trade || !preset || !cfg) return draft;

  const scopes = { ...draft.scopes };
  const elements = { ...draft.elements };
  const modifiers = { ...draft.modifiers };

  if (preset.scopeIds) {
    const controlled = new Set();
    cfg.presets.forEach(p => (p.scopeIds || []).forEach(id => controlled.add(String(id))));
    const want = new Set((preset.scopeIds || []).map(String));

    (trade.scopes || []).forEach(sc => {
      const id = String(sc.scope_id);
      if (!controlled.has(id)) return;
      const elementIds = (sc.elements || []).map(e => e.id);
      const modifierIds = (sc.modifiers || []).map(m => m.id).filter(Boolean);

      if (want.has(id)) {
        scopes[sc.scope_id] = true;
        (sc.elements || []).forEach(el => {
          const key = String(el.id);
          if (Object.prototype.hasOwnProperty.call(preset.elementMap || {}, key)) {
            if (preset.elementMap[key]) elements[el.id] = true;
            else delete elements[el.id];
          } else if (!elementDistinctiveOnOtherPreset(cfg, preset, el.id)) {
            elements[el.id] = true;
          }
        });
        (sc.modifiers || []).forEach(m => {
          if (m.modifier_type === 'example') return;
          const ans = answerForModifier(preset.answersByModifierId, m);
          if (ans === undefined) return;
          const writeKey = modifierWriteKey(m);
          if (writeKey != null) modifiers[writeKey] = toModifierValue(m, ans);
        });
      } else {
        delete scopes[sc.scope_id];
        elementIds.forEach(eid => delete elements[eid]);
        modifierIds.forEach(mid => delete modifiers[mid]);
      }
    });

    (trade.trade_modifiers || []).forEach(m => {
      if (m.modifier_type === 'example') return;
      const ans = answerForModifier(preset.answersByModifierId, m);
      if (ans === undefined) return;
      const writeKey = modifierWriteKey(m);
      if (writeKey != null) modifiers[writeKey] = toModifierValue(m, ans);
    });

    const keepMods = new Set(Object.keys(preset.answersByModifierId || {}).map(String));
    cfg.presets.forEach(p => {
      if (String(p.id) === String(preset.id)) return;
      Object.keys(p.answersByModifierId || {}).forEach(modId => {
        if (!keepMods.has(String(modId))) delete modifiers[modId];
      });
    });

    return { ...draft, scopes, elements, modifiers };
  }

  // Fixtures — name-based apply.
  const controlled = new Set();
  cfg.presets.forEach(p => (p.items || []).forEach(x => controlled.add(norm(x))));
  const want = new Set((preset.items || []).map(norm));

  (trade.scopes || []).forEach(sc => {
    const name = norm(sc.scope_name);
    if (!controlled.has(name)) return;
    const elementIds = (sc.elements || []).map(e => e.id);
    const modifierIds = (sc.modifiers || []).map(m => m.id).filter(Boolean);

    if (want.has(name)) {
      scopes[sc.scope_id] = true;
      elementIds.forEach(id => {
        elements[id] = true;
      });
      (sc.modifiers || []).forEach(m => {
        if (m.modifier_type === 'example') return;
        const ans = findAnswer(preset.answers, m.title);
        if (ans === undefined) return;
        modifiers[m.id] = toModifierValue(m, ans);
      });
    } else {
      delete scopes[sc.scope_id];
      elementIds.forEach(id => delete elements[id]);
      modifierIds.forEach(id => delete modifiers[id]);
    }
  });

  (trade.trade_modifiers || []).forEach(m => {
    if (m.modifier_type === 'example') return;
    const ans = findAnswer(preset.tradeAnswers, m.title);
    if (ans === undefined) return;
    modifiers[m.id] = toModifierValue(m, ans);
  });

  return { ...draft, scopes, elements, modifiers };
};

/**
 * When the UI-active / A9-suggested preset changes, apply its full scopes + elements
 * (and answers). If nothing is active, only clear chosenPresets — leave picks alone.
 * Returns null when there is nothing to change.
 */
export const syncDraftToActivePreset = ({ draft, trade, cfg, active, previousChosenId }) => {
  if (!draft || !trade || !cfg) return null;
  const prev = previousChosenId != null && previousChosenId !== '' ? String(previousChosenId) : '';
  if (!active) {
    if (!prev) return null;
    const chosenPresets = { ...(draft.chosenPresets || {}) };
    delete chosenPresets[trade.trade_id];
    return { ...draft, chosenPresets };
  }
  if (prev === String(active.id)) return null;
  const applied = applyPresetToDraft(draft, trade, active, cfg);
  return {
    ...applied,
    chosenPresets: {
      ...(applied.chosenPresets || draft.chosenPresets || {}),
      [trade.trade_id]: String(active.id)
    }
  };
};

/** Apply a scope checkbox toggle to a draft (on or off). */
export const toggleScopeInDraft = (draft, scopeId, elementIds = [], modifierIds = []) => {
  const scopes = { ...draft.scopes };
  const elements = { ...draft.elements };
  const modifiers = { ...draft.modifiers };
  if (scopes[scopeId]) {
    delete scopes[scopeId];
    elementIds.forEach(id => delete elements[id]);
    modifierIds.forEach(id => delete modifiers[id]);
  } else {
    scopes[scopeId] = true;
    elementIds.forEach(id => {
      elements[id] = true;
    });
  }
  return { ...draft, scopes, elements, modifiers };
};

/**
 * When turning a scope on, only auto-enable elements that the lightest owning
 * preset lists as is_on:true (or does not list). Preset is_on:false stay off
 * so we don't immediately climb to a heavier preset.
 */
const elementIdsForScopeOn = (cfg, scopeId, elementIds) => {
  if (!cfg?.presets?.length || !elementIds?.length) return elementIds || [];
  const { ownerPackage, ladder } = buildPresetPackages(cfg);
  const owner =
    ownerPackage.get(String(scopeId)) || ladder.find(p => (p.scopeIds || []).map(String).includes(String(scopeId)));
  if (!owner?.elementMap) return elementIds;
  return elementIds.filter(id => {
    const key = String(id);
    if (!Object.prototype.hasOwnProperty.call(owner.elementMap, key)) return true;
    return owner.elementMap[key] === true;
  });
};

/**
 * Scope checkbox: toggle only the clicked scope/elements, suggest a preset (never select it).
 * Keeps an existing chosen preset so extras outside it can suggest another preset.
 */
export const applyScopeToggleWithPresets = (draft, trade, cfg, scopeId, elementIds = [], modifierIds = []) => {
  if (!draft || !trade) {
    return { draft, presetId: null, suggestedPresetId: null };
  }

  const turningOn = !draft.scopes?.[scopeId];
  const idsToToggle = turningOn ? elementIdsForScopeOn(cfg, scopeId, elementIds) : elementIds;
  const toggled = toggleScopeInDraft(draft, scopeId, idsToToggle, modifierIds);
  const chosenPresetId = draft.chosenPresets?.[trade.trade_id] || null;

  if (!cfg?.presets?.length) {
    return { draft: toggled, presetId: null, suggestedPresetId: null };
  }

  const picks = picksFromDraft(trade, toggled);
  const suggested = matchPresetFromPicks(cfg, picks, { chosenPresetId });
  return {
    draft: toggled,
    presetId: null,
    suggestedPresetId: suggested ? String(suggested.id) : null
  };
};

/** Apply a single element chip toggle to a draft. */
export const toggleElementInDraft = (draft, elementId) => {
  const elements = { ...draft.elements };
  if (elements[elementId]) delete elements[elementId];
  else elements[elementId] = true;
  return { ...draft, elements };
};
